import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, withOptionalCompanyId } from '@/shared/api/base';
import type { ConversationListResponse } from '@/shared/api/types/schema.ts';

export const CONVERSATION_KEYS = {
  all: (meetingGroupId?: string, companyId?: string | null) =>
    companyId
      ? ['conversation', 'list', meetingGroupId ?? null, companyId]
      : ['conversation', 'list', meetingGroupId ?? null],
  detailed: (conversationId: string, companyId?: string | null) =>
    companyId
      ? ['conversation', 'detailed', conversationId, companyId]
      : ['conversation', 'detailed', conversationId],
  active: (companyId?: string | null) =>
    companyId ? ['conversation', 'active', companyId] : ['conversation', 'active'],
  activeMeetRoomsWithoutConversation: (companyId?: string | null) =>
    companyId
      ? ['conversation', 'active-meet-rooms-no-conversation', companyId]
      : ['conversation', 'active-meet-rooms-no-conversation'],
};

export interface ConversationInviteUserResult {
  user_id: string;
  display_name?: string | null;
  status: string;
  sent_tokens: number;
  total_tokens: number;
  error?: string | null;
}

export interface ConversationInviteResponse {
  conversation_id: string;
  invited_count: number;
  skipped_count: number;
  results: ConversationInviteUserResult[];
}

export interface ActiveMeetRoomItem {
  workspace_id: string;
  meeting_group_id: string | null;
  workspace_name: string | null;
  room_id: string;
  room_url: string | null;
  participants_count: number;
  participant_display_names: string[];
}

export interface ActiveMeetRoomListResponse {
  items: ActiveMeetRoomItem[];
}

export const useGetConversations = (
  meetingGroupById: string,
  companyId?: string | null,
  enabled: boolean = true,
) => {
  const { data, isError, isFetching, isPending, isSuccess } = useQuery({
    queryKey: CONVERSATION_KEYS.all(meetingGroupById, companyId),
    queryFn: () =>
      api.instance.get<ConversationListResponse>('/conversations', {
        params: {
          meeting_group_id: meetingGroupById,
          company_id: companyId ?? undefined,
        },
      }),
    enabled: Boolean(meetingGroupById) && enabled,
  });

  return {
    data: data?.data,
    isError,
    isFetching,
    isPending,
    isSuccess,
  };
};

export const useGetConversationById = (sessionsId: string, companyId?: string | null) => {
  const { data, isError, isFetching, isPending, isSuccess } = useQuery({
    queryKey: CONVERSATION_KEYS.detailed(sessionsId, companyId),
    queryFn: () =>
      api.conversations.getConversationConversationsConversationIdGet(
        sessionsId,
        withOptionalCompanyId(companyId),
      ),
    enabled: Boolean(sessionsId),
  });

  return {
    data: data?.data,
    isError,
    isFetching,
    isPending,
    isSuccess,
  };
};

export const useDeleteConversationMutation = (
  meetingGroupById: string,
  companyId?: string | null,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) =>
      api.conversations.deleteConversationConversationsConversationIdDelete(
        conversationId,
        withOptionalCompanyId(companyId),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CONVERSATION_KEYS.all(meetingGroupById, companyId),
      });
    },
  });
};

export const useGetConversationStatus = (conversationId?: string, companyId?: string | null) => {
  const query = useQuery({
    queryKey: CONVERSATION_KEYS.detailed(conversationId ?? 'unknown', companyId),
    queryFn: () =>
      api.conversations.getConversationConversationsConversationIdGet(
        conversationId ?? '',
        withOptionalCompanyId(companyId),
      ),
    enabled: Boolean(conversationId),
  });

  return {
    data: query.data?.data,
    isError: query.isError,
    isFetching: query.isFetching,
    isPending: query.isPending,
    isSuccess: query.isSuccess,
  };
};

export const useGetActiveConversations = (companyId?: string | null) => {
  const { data, isError, isFetching, isPending, isSuccess, refetch } = useQuery({
    queryKey: CONVERSATION_KEYS.active(companyId),
    queryFn: () =>
      api.instance.get<ConversationListResponse>('/conversations', {
        params: {
          status: 'active',
          company_id: companyId,
        },
      }),
    enabled: Boolean(companyId),
    refetchInterval: 15000,
  });
  return { data: data?.data, isError, isFetching, isPending, isSuccess, refetch };
};

export const useGetActiveMeetRoomsWithoutConversation = (companyId?: string | null) => {
  const { data, isError, isFetching, isPending, isSuccess, refetch } = useQuery({
    queryKey: CONVERSATION_KEYS.activeMeetRoomsWithoutConversation(companyId),
    queryFn: async () => {
      const response = await api.instance.get<ActiveMeetRoomListResponse>(
        '/providers/meet/active-rooms',
        { params: { company_id: companyId } },
      );
      return response.data;
    },
    enabled: Boolean(companyId),
    refetchInterval: 15000,
  });

  return { data, isError, isFetching, isPending, isSuccess, refetch };
};

export const useFinalizeConversationMutation = (
  meetingGroupById?: string,
  companyId?: string | null,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) =>
      api.conversations.finalizeConversationConversationsConversationIdFinalizePost(
        conversationId,
        {},
        withOptionalCompanyId(companyId),
      ),
    onSuccess: (_data, conversationId) => {
      if (meetingGroupById) {
        queryClient.invalidateQueries({
          queryKey: CONVERSATION_KEYS.all(meetingGroupById, companyId),
        });
      }

      queryClient.invalidateQueries({
        queryKey: CONVERSATION_KEYS.detailed(conversationId, companyId),
      });
    },
  });
};

export const inviteGroupMembersToConversation = async (
  conversationId: string,
  userIds: string[],
): Promise<ConversationInviteResponse> => {
  const { data } = await api.instance.post<ConversationInviteResponse>(
    `/api/v1/push/conversations/${conversationId}/invite`,
    { user_ids: userIds },
  );
  if (!data) {
    throw new Error('Invite response is empty');
  }
  return data;
};

export const useInviteConversationMembersMutation = (conversationId: string) => {
  return useMutation({
    mutationFn: (userIds: string[]) => inviteGroupMembersToConversation(conversationId, userIds),
  });
};
